import { ErrorOutline, FileCopyOutlined } from "@mui/icons-material";
import {
  Button,
  Paper,
  Popper,
  Stack,
  styled,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import shellQuote from "shell-quote";

import { ValidationTextField } from "@components/ValidationContext/ValidationContext";

import { commandHelpText, validateCommand } from "../utils";
import { fieldStyle, wideFieldStyle } from "./NewJobFormLayout";

type Props = {
  defaultValue: string;
  onUpdate: (value: string) => void;
};

const HelpPopper = styled(Popper)({ zIndex: 1 });

const HelpFlyout = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  ...fieldStyle,
}));

const CommandSuggestionText = styled("code")(({ theme }) => ({
  flexGrow: 1,
  backgroundColor: theme.palette.background.default,
  padding: theme.spacing(1),
  whiteSpace: "pre-wrap",
  borderRadius: theme.shape.borderRadius,
  marginRight: theme.spacing(1),
}));

const CommandField = React.memo(function CommandField({
  defaultValue,
  onUpdate,
}: Props) {
  const [inputValue, setInputValue] = useState(defaultValue);

  const commandError = useMemo(
    () => validateCommand(inputValue) ?? null,
    [inputValue],
  );

  const commandSuggestion = useMemo(
    () => (commandError ? shellQuote.quote(["/bin/sh", "-c", inputValue]) : ""),
    [commandError, inputValue],
  );

  const [helpFlyoutOpened, setHelpFlyoutOpened] = useState(false);

  useEffect(() => {
    if (!commandError) {
      setHelpFlyoutOpened(false);
    }
  }, [commandError]);

  const [buttonRef, setButtonRef] = useState<HTMLElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function listener(e: Event) {
      const target = e.target as Node;
      if (
        buttonRef?.contains(target) ||
        inputRef.current?.contains(target) ||
        flyoutRef.current?.contains(target)
      ) {
        return;
      }
      setHelpFlyoutOpened(false);
    }
    window.addEventListener("click", listener);
    return () => {
      window.removeEventListener("click", listener);
    };
  }, [buttonRef]);

  return (
    <Stack direction="row" spacing={1} alignItems="start" sx={{ pb: 2 }}>
      <ValidationTextField
        instanceId="command"
        inputRef={inputRef}
        label="Command"
        variant="outlined"
        multiline
        maxRows={10}
        placeholder="use default"
        InputLabelProps={{ shrink: true }}
        defaultValue={defaultValue}
        valueForValidation={inputValue}
        onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) =>
          onUpdate(e.target.value)
        }
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
          setInputValue(e.target.value)
        }
        helperText={commandHelpText}
        error={commandError}
        sx={wideFieldStyle}
      />
      {commandError && (
        <>
          <Button
            ref={setButtonRef}
            onClick={() => setHelpFlyoutOpened((opened) => !opened)}
            sx={{ mt: "6px" }}
          >
            <ErrorOutline color="error" />
          </Button>
          <HelpPopper
            anchorEl={buttonRef}
            open={helpFlyoutOpened}
            placement="right"
          >
            <HelpFlyout elevation={4} ref={flyoutRef}>
              <Typography variant="body1" color="error">
                Try passing your command to a shell
              </Typography>
              <Stack direction="row" alignItems="center" sx={{ pt: 1 }}>
                <CommandSuggestionText>
                  {commandSuggestion}
                </CommandSuggestionText>
                <Button
                  color="primary"
                  onClick={() =>
                    navigator.clipboard.writeText(commandSuggestion)
                  }
                >
                  <FileCopyOutlined />
                </Button>
              </Stack>
            </HelpFlyout>
          </HelpPopper>
        </>
      )}
    </Stack>
  );
});

export default CommandField;
